import React, { createContext, useContext, useEffect, useMemo } from 'react'

import { UseFormReturn } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import { RegisterRequestDTO } from '@/core/api/dtos/request/auth/register'

type StepNumber = number

interface RegisterFlowContextValue {
	currentStep: StepNumber
	goToStep: (step: StepNumber, opts?: { replace?: boolean }) => void
	next: () => void
	prev: () => void
}

const RegisterFlowContext = createContext<RegisterFlowContextValue | null>(null)

const clampStep = (step: number, min: number, max: number) =>
	Math.min(Math.max(step, min), max)

const parseStepParam = (stepParam: string | undefined) => {
	const parsed = Number(stepParam)
	if (!Number.isFinite(parsed)) return 0
	return parsed
}

const isStep1Valid = (form: UseFormReturn<RegisterRequestDTO>) => {
	const firstName = form.getValues('firstname') || ''
	const lastName = form.getValues('lastname') || ''
	const email = form.getValues('email') || ''
	const password = form.getValues('password') || ''
	const passwordConfirmation = form.getValues('password_confirmation') || ''
	return (
		firstName.length > 0 &&
		lastName.length > 0 &&
		email.length > 0 &&
		password.length > 0 &&
		passwordConfirmation.length > 0
	)
}

// Step 2 (Music Priority) has no form validation - it's just a PostHog tracking step
const isStep2Valid = () => {
	return true // Always valid - user just needs to select an option to proceed
}

// Step 3: Phone/Country
const isStep3Valid = (form: UseFormReturn<RegisterRequestDTO>) => {
	const phoneNumber = form.getValues('phone_number') || ''
	const country = form.getValues('country')
	return phoneNumber.length > 0 && !!country
}

// Step 4: Brand name/stores
const isStep4Valid = (form: UseFormReturn<RegisterRequestDTO>) => {
	const brandName = form.getValues('brand_name') || ''
	const totalStores = form.getValues('total_company_stores')
	return brandName.length > 0 && !!totalStores && totalStores > 0
}

// Step 5: Business category
const isStep5Valid = (form: UseFormReturn<RegisterRequestDTO>) => {
	const parentCategoryId = form.getValues('parent_business_category_id')
	const categoryId = form.getValues('business_category_id')
	return !!parentCategoryId && !!categoryId
}

// Step 6: Demographics
const isStep6Valid = (form: UseFormReturn<RegisterRequestDTO>) => {
	const demographicAges = form.getValues('demographic_ages')
	const demographicSocioeconomics = form.getValues('demographic_socioeconomics')
	return demographicAges.length > 0 && demographicSocioeconomics.length > 0
}

/**
 * Returns the first step that is NOT completed yet.
 * This mirrors the existing RegisterUI gating behavior (steps 1–7),
 * but the router guard uses it to decide if a step deep-link is allowed.
 */
const getFirstIncompletePreRegistrationStep = (
	form: UseFormReturn<RegisterRequestDTO>
): StepNumber => {
	if (!isStep1Valid(form)) return 1
	if (!isStep2Valid()) return 2 // Music Priority - always passes
	if (!isStep3Valid(form)) return 3
	if (!isStep4Valid(form)) return 4
	if (!isStep5Valid(form)) return 5
	if (!isStep6Valid(form)) return 6
	return 7
}

interface RegisterFlowProviderProps {
	children: React.ReactNode
	form: UseFormReturn<RegisterRequestDTO>
	/**
	 * Maximum step index in the flow.
	 * (Default 7, corresponding to /register/step/7)
	 */
	maxStep?: number
}

export const RegisterFlowProvider: React.FC<RegisterFlowProviderProps> = ({
	children,
	form,
	maxStep = 7,
}) => {
	const navigate = useNavigate()
	const params = useParams()

	// NOTE: These are intentionally computed on every render so they always reflect
	// the latest `react-hook-form` values (the `form` object identity is stable).
	const rawRequestedStep = parseStepParam(params.step)
	const requestedStep = clampStep(rawRequestedStep, 0, maxStep)

	const isStepAllowed = (step: number) => {
		const normalized = clampStep(step, 0, maxStep)

		const firstIncompletePreRegistrationStep =
			getFirstIncompletePreRegistrationStep(form)

		// Allow steps up to (and including) the first incomplete one.
		// Example: if first incomplete is 3, then 0..3 are allowed.
		return normalized <= firstIncompletePreRegistrationStep
	}

	const goToStep = (step: StepNumber, opts?: { replace?: boolean }) => {
		const normalized = clampStep(step, 0, maxStep)
		if (!isStepAllowed(normalized)) {
			navigate('/register/step/0', { replace: true })
			return
		}

		navigate(`/register/step/${normalized}`, { replace: opts?.replace })
	}

	const next = () => {
		goToStep(requestedStep + 1)
	}

	const prev = () => {
		if (requestedStep <= 0) return
		goToStep(requestedStep - 1)
	}

	// Guard: redirect invalid deep links back to step 0.
	useEffect(() => {
		if (!isStepAllowed(requestedStep)) {
			navigate('/register/step/0', { replace: true })
		}
	}, [requestedStep])

	const value = useMemo<RegisterFlowContextValue>(
		() => ({
			currentStep: requestedStep,
			goToStep,
			next,
			prev,
		}),
		[requestedStep]
	)

	return (
		<RegisterFlowContext.Provider value={value}>
			{children}
		</RegisterFlowContext.Provider>
	)
}

export const useRegisterFlow = () => {
	const ctx = useContext(RegisterFlowContext)
	if (!ctx) {
		throw new Error('useRegisterFlow must be used within RegisterFlowProvider')
	}
	return ctx
}
